import React, { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import {
  getTrip,
  assignSubvendorResponse,
  getDriverAssigns,
  assignDriverToTrip,
} from "redux/actions/UserTrip";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { dateFormate } from "utils/utils";
import RideTrackTab from "./RideTrackTab";
import { filterRideTrackdata } from "./RideTrackData";
// MUI Modal Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
  TextField,
} from "@mui/material";

// ── Reject Reason Dialog ──────────────────────────────────────────────────────
const RejectReasonDialog = ({ open, onClose, onSubmit }) => {
  const [reason, setReason] = React.useState("");

  const handleSubmit = () => {
    if (reason.trim()) {
      onSubmit(reason.trim());
      setReason("");
    }
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "18px", color: "#1a237e" }}>
        Reject Trip Assignment
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Please specify the reason for rejecting this trip assignment.
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label="Rejection Reason"
          type="text"
          fullWidth
          variant="outlined"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          sx={{ color: "grey.600", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={!reason.trim()}
          sx={{ borderRadius: "8px", px: 3, textTransform: "none" }}
        >
          Reject Trip
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Re-Allot Reason Dialog ────────────────────────────────────────────────────
const ReAllotReasonDialog = ({ open, onClose, onSubmit }) => {
  const [reason, setReason] = React.useState("");

  const handleSubmit = () => {
    if (reason.trim()) {
      onSubmit(reason.trim());
      setReason("");
    }
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "18px", color: "#1a237e" }}>
        Reason for Re-Allotment
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Please specify why you are re-allotting this trip to a different driver.
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label="Re-Allotment Reason"
          type="text"
          fullWidth
          variant="outlined"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: "grey.600", textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!reason.trim()}
          sx={{ borderRadius: "8px", px: 3, textTransform: "none" }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

export default function RideTrack() {
  const tripStatus = JSON.parse(localStorage.getItem("tripStatus"));
  const { state } = useLocation() || {};
  let rideTrackStatus = state?.rideTrackStatus;
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams?.get("startDate");
  const endDate = searchParams?.get("endDate");
  const filterPage = searchParams?.get("page");
  const filterRowPerPage = searchParams?.get("rowPerPage");
  const filterSearch = searchParams?.get("search");
  const { tabs } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [value, setValue] = useState("1");
  const [page, setPage] = useState(filterPage ? parseInt(filterPage) : 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    filterRowPerPage ? filterRowPerPage : 10,
  );
  const [dateFrom, setDateFrom] = useState(
    startDate && startDate !== "null"
      ? startDate
      : endDate === "null"
        ? ""
        : state?.fromTab
          ? state?.dateFrom
          : dateFormate(new Date(), "MM/DD/YYYY"),
  );
  const [dateTo, setDateTo] = useState(
    endDate && endDate !== "null"
      ? endDate
      : endDate === "null"
        ? ""
        : state?.fromTab
          ? state?.dateTo
          : dateFormate(new Date(), "MM/DD/YYYY"),
  );
  const [status, setStatus] = useState(
    tripStatus?.length > 0
      ? tripStatus
      : rideTrackStatus?.length > 0
        ? [rideTrackStatus]
        : "",
  );
  const [search, setSearch] = useState(filterSearch ? filterSearch : "");
  const [type, setType] = useState("ride");

  const tableData = useSelector((state) => state?.userTrip?.userTrip?.data);
  const loader = useSelector((state) => state?.userTrip?.loading);
  const driversList = useSelector(
    (state) => state?.userTrip?.driversList || [],
  );
  const driversLoading = useSelector(
    (state) => state?.userTrip?.driversLoading || false,
  );

  // Allot Vehicle Modal States
  const [allotModalOpen, setAllotModalOpen] = useState(false);
  const [currentTripId, setCurrentTripId] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");

  // Accept Confirmation Dialog States
  const [acceptConfirmOpen, setAcceptConfirmOpen] = useState(false);
  const [pendingAcceptTripId, setPendingAcceptTripId] = useState(null);

  // Reject Reason Dialog States
  const [rejectReasonOpen, setRejectReasonOpen] = useState(false);
  const [rejectTripId, setRejectTripId] = useState(null);

  // Re-Allot Reason Dialog States
  const [reAllotReasonOpen, setReAllotReasonOpen] = useState(false);
  const [reAllotTripRow, setReAllotTripRow] = useState(null); // full row data for re-allot
  const [reAllotReason, setReAllotReason] = useState("");
  const [currentAssignedDriverId, setCurrentAssignedDriverId] = useState(null);

  let filterData = {
    id: "",
    page: page,
    dateFrom: dateFrom,
    dateTo: dateTo,
    status: status,
    pageSize: rowsPerPage,
    type: type,
    search: search,
  };

  let paramsList = {};
  searchParams.forEach((value, key) => {
    paramsList[key] = value;
  });

  const businessType = localStorage.getItem("businessType");

  useEffect(() => {
    if (businessType === "isB2B" && tabs === "1") {
      navigate(`/rideTrack/2`);
      window.location.reload();
    } else if (businessType === "isB2C" && tabs === "4") {
      navigate(`/rideTrack/1`);
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (tabs == "2") {
      setType("rental");
      dispatch(getTrip({ ...filterData, type: "rental" }));
    } else if (tabs == "3") {
      setType("airport");
      dispatch(getTrip({ ...filterData, type: "airport" }));
    } else if (tabs == "4") {
      setType("outstation");
      dispatch(getTrip({ ...filterData, type: "outstation" }));
    } else {
      setType("ride");
      dispatch(getTrip({ ...filterData, type: "ride" }));
    }
    setValue(tabs);
  }, []);
  const transformedTableData = useMemo(() => {
    if (!tableData) return null;

    // 1. Update the headers to include correct formatting flags
    const updatedHeader = tableData.header.map((col) => {
      if (col.label === "Trip Date") {
        return { ...col, key: "scheduledTime", isDateFormat: "scheduledTime" };
      }
      if (col.label === "Reporting Time") {
        return { ...col, key: "scheduledTime", isTimeFormat: "scheduledTime" };
      }
      return col;
    });

    // 2. Ensure the 3-dot icon column is present (same pattern as Ops)
    const hasIcon = updatedHeader.some((col) => col.key === "icon");
    if (!hasIcon) {
      updatedHeader.push({ label: "", key: "icon", className: "iconColumn" });
    }

    // 3. Map tripScheduledTime to scheduledTime in the body so the table can find it
    const updatedBody = tableData.body.map((trip) => ({
      ...trip,
      scheduledTime: trip.tripScheduledTime,
    }));

    return { ...tableData, header: updatedHeader, body: updatedBody };
  }, [tableData]);

  const handleChange = (newValue) => {
    localStorage.removeItem("tripStatus");
    navigate(`/rideTrack/${newValue}`);
    setStatus([]);
    setSearch("");
    if (newValue == "2") {
      setType("rental");
      dispatch(
        getTrip({ ...filterData, type: "rental", status: "", search: "" }),
      );
    } else if (newValue == "3") {
      setType("airport");
      dispatch(
        getTrip({ ...filterData, type: "airport", status: "", search: "" }),
      );
    } else if (newValue == "4") {
      setType("outstation");
      dispatch(
        getTrip({ ...filterData, type: "outstation", status: "", search: "" }),
      );
    } else {
      setType("ride");
      dispatch(
        getTrip({ ...filterData, type: "ride", status: "", search: "" }),
      );
    }
    setValue(newValue);
  };

  const handleChangePage = (newPage) => {
    setSearchParams({ ...paramsList, page: newPage });
    setPage(newPage);
    dispatch(getTrip({ ...filterData, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setSearchParams({
      ...paramsList,
      rowPerPage: parseInt(event.target.value),
      page: 0,
    });
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    dispatch(
      getTrip({
        ...filterData,
        page: 0,
        pageSize: parseInt(event.target.value),
      }),
    );
  };

  const onClickTableVendor = (data) => {
    localStorage.setItem("tripStatus", JSON.stringify(status));
    let url = window.location.pathname.split("/")[1];
    if (url === "rideTrack" && businessType === "isB2C") {
      navigate("/rideTrack/details", {
        state: {
          id: data?.id,
          status: data?.status,
          customer: data?.customerName,
          driver: data?.driverName,
          type,
          tripId: data?.tripId,
        },
      });
    } else if (url === "rideTrack" && businessType === "isB2B") {
      navigate(
        `/rideTrack/bookingdetails?id=${data.id}&startDate=${dateFrom}&endDate=${dateTo}&status=${status}&search=${search}&page=${page}&rowPerPage=${rowsPerPage}&tab=${tabs}`,
        { state: { id: data?.id } },
      );
    }
  };

  const onFilterChange = (val) => {
    rideTrackStatus = "";
    localStorage.removeItem("tripStatus");
    setStatus(val);
    setPage(0);
    setSearchParams({ ...paramsList, status: val?.toString() });
    filterData = { ...filterData, status: val.toString(), page: 0 };
    dispatch(getTrip(filterData));
  };

  const onRestFilter = () => {
    rideTrackStatus = "";
    localStorage.removeItem("tripStatus");
    setStatus("");
    setSearchParams({ ...paramsList, status: "" });
    filterData = { ...filterData, status: "" };
    dispatch(getTrip(filterData));
  };

  const onSelectDateRange = (newValue) => {
    if (newValue === null || newValue === 0) {
      setSearchParams({ ...paramsList, startDate: "null", endDate: "null" });
      setDateFrom("");
      setDateTo("");
      dispatch(getTrip({ ...filterData, dateFrom: "", dateTo: "" }));
    } else {
      const newFrom = dateFormate(newValue[0], "MM/DD/YYYY");
      const newTo = dateFormate(newValue[1], "MM/DD/YYYY");

      setSearchParams({
        ...paramsList,
        startDate: newFrom,
        endDate: newTo,
      });

      setDateFrom(newFrom);
      setDateTo(newTo);

      // Dispatch with newFrom and newTo directly to avoid stale state issues
      dispatch(getTrip({ ...filterData, dateFrom: newFrom, dateTo: newTo }));
    }
  };

  const onSearch = debounce((e) => {
    setSearchParams({ ...paramsList, page: 0, search: e });
    setSearch(e);
    setPage(0);
    filterData = { ...filterData, search: e, pageSize: "", page: 0 };
    dispatch(getTrip(filterData));
  }, 1000);

  // --- SUB-VENDOR HANDLERS ---

  const handleAcceptTrip = async (tripId) => {
    try {
      await dispatch(assignSubvendorResponse({ tripId, action: "accept" }));
      setPendingAcceptTripId(tripId);
      setAcceptConfirmOpen(true);
    } catch (error) {
      console.error("Accept failed", error);
    }
  };

  const handleRejectTrip = (tripId) => {
    setRejectTripId(tripId);
    setRejectReasonOpen(true);
  };

  const handleConfirmReject = async (reason) => {
    try {
      await dispatch(
        assignSubvendorResponse({
          tripId: rejectTripId,
          action: "reject",
          rejectionReason: reason,
        }),
      );
      setRejectReasonOpen(false);
      setRejectTripId(null);
      dispatch(getTrip(filterData));
    } catch (error) {
      console.error("Reject failed", error);
    }
  };

  // --- Unified 3-dot menu handler (mirrors Ops AllRides pattern) ---
  const handleClickMoreIcon = (rowData, actionType) => {
    switch (actionType) {
      case "Allot":
        handleOpenAllotModal(rowData?.id, false, null);
        break;
      case "Re-Allot":
        // For re-allot: collect reason first, then open driver modal
        setReAllotTripRow(rowData);
        setCurrentAssignedDriverId(
          rowData?.driverId ||
          rowData?.driver?.id ||
          rowData?.DriverProfile?.id ||
          null
        );
        setReAllotReasonOpen(true);
        break;
      case "Accept":
        handleAcceptTrip(rowData?.id);
        break;
      case "Reject":
        handleRejectTrip(rowData?.id);
        break;
      default:
        break;
    }
  };

  // Called after reason is confirmed — open driver selection modal
  const handleReAllotReasonConfirm = async (reason) => {
    setReAllotReason(reason);
    setReAllotReasonOpen(false);
    await handleOpenAllotModal(reAllotTripRow?.id, true, currentAssignedDriverId);
  };

  const handleAllotNow = () => {
    setAcceptConfirmOpen(false);
    handleOpenAllotModal(pendingAcceptTripId);
    setPendingAcceptTripId(null);
  };

  const handleAllotLater = () => {
    setAcceptConfirmOpen(false);
    setPendingAcceptTripId(null);
    dispatch(getTrip(filterData));
  };

  const handleOpenAllotModal = async (tripId, isReAllot = false, assignedDriverId = null) => {
    setCurrentTripId(tripId);
    setCurrentAssignedDriverId(assignedDriverId);
    await dispatch(getDriverAssigns({ tripId }));
    setAllotModalOpen(true);
  };

  const handleCloseAllotModal = () => {
    setAllotModalOpen(false);
    setSelectedDriver("");
    setCurrentTripId("");
    setReAllotReason("");
    setReAllotTripRow(null);
    setCurrentAssignedDriverId(null);
    dispatch(getTrip(filterData));
  };

  const handleConfirmAllot = async () => {
    // Robust find function to handle various ID key names from API
    const driverData = driversList.find(
      (d) => (d.driverId || d.id || d._id) === selectedDriver,
    );

    if (!driverData) return;

    // Extract IDs robustly
    const finalDriverId =
      driverData.driverId || driverData.id || driverData._id;
    const finalVehicleId =
      driverData.vehicleId ||
      driverData.vehicle?._id ||
      driverData.vehicleDetails?._id;

    if (finalDriverId === currentAssignedDriverId) {
      toast.error("This driver is already assigned to this trip. Please select a different driver.");
      return;
    }

    const payload = {
      tripId: currentTripId,
      driverId: finalDriverId,
      vehicleId: finalVehicleId,
    };
    // Pass reason only for re-allot
    if (reAllotReason) {
      payload.reason = reAllotReason;
    }

    try {
      await dispatch(assignDriverToTrip(payload));
      handleCloseAllotModal();
    } catch (error) {
      console.error("Assign driver failed", error);
    }
  };

  return (
    <div>
      <RideTrackTab
        defaultStatus={status}
        filterData={filterRideTrackdata}
        rideTrackData={transformedTableData}
        onClickTableVendor={onClickTableVendor}
        handleChange={handleChange}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        value={value}
        page={page}
        rowsPerPage={rowsPerPage}
        onFilterChange={onFilterChange}
        onRestFilter={onRestFilter}
        onSelectDateRange={onSelectDateRange}
        loader={loader}
        onSearch={onSearch}
        tabName={"rideTrack"}
        dateFrom={dateFrom}
        dateTo={dateTo}
        search={search}
        onClickMoreIcon={handleClickMoreIcon}
      />

      {/* ── Accept Confirmation Dialog ── */}
      <Dialog
        open={acceptConfirmOpen}
        onClose={handleAllotLater}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon color="success" />
          Trip Accepted
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Trip has been accepted successfully. Do you want to allot a vehicle
            now?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAllotLater} color="inherit" variant="outlined">
            Later
          </Button>
          <Button
            onClick={handleAllotNow}
            color="primary"
            variant="contained"
            autoFocus
          >
            Allot Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Allot / Re-Allot Vehicle Modal ── */}
      <Dialog
        open={allotModalOpen}
        onClose={handleCloseAllotModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{reAllotReason ? "Re-Allot Vehicle" : "Allot Vehicle"}</DialogTitle>
        <DialogContent>
          {driversLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <FormControl fullWidth sx={{ mt: 2, minWidth: 300 }}>
              <InputLabel>Select Driver</InputLabel>
              <Select
                value={selectedDriver}
                label="Select Driver"
                onChange={(e) => setSelectedDriver(e.target.value)}
              >
                {driversList?.filter((driver) => {
                  // On re-allot: exclude the currently assigned driver
                  if (!currentAssignedDriverId) return true;
                  const dId = driver.driverId || driver.id || driver._id;
                  return dId !== currentAssignedDriverId;
                }).length > 0 ? (
                  driversList
                    .filter((driver) => {
                      if (!currentAssignedDriverId) return true;
                      const dId = driver.driverId || driver.id || driver._id;
                      return dId !== currentAssignedDriverId;
                    })
                    .map((driver, index) => {
                      // Robust extraction to avoid undefined values which break MUI Select
                      const dId =
                        driver.driverId ||
                        driver.id ||
                        driver._id ||
                        `driver-${index}`;
                      const dName =
                        driver.driverName ||
                        driver.name ||
                        `${driver.firstName || ""} ${driver.lastName || ""}`.trim() ||
                        "Unknown Driver";
                      const vName =
                        driver.vehicleName ||
                        driver.vehicle?.name ||
                        driver.carType ||
                        driver.vehicleDetails?.name ||
                        "Vehicle";
                      const vNum =
                        driver.regNumber ||
                        driver.vehicleNumber ||
                        driver.vehicle?.regNumber ||
                        "";

                      return (
                        <MenuItem key={dId} value={dId}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {dName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {vName} {vNum && `• ${vNum}`}
                            </Typography>
                          </Box>
                        </MenuItem>
                      );
                    })
                ) : (
                  <MenuItem disabled>No other drivers available</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAllotModal} color="error">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAllot}
            color="primary"
            variant="contained"
            disabled={!selectedDriver || driversLoading}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Reason Dialog ── */}
      <RejectReasonDialog
        open={rejectReasonOpen}
        onClose={() => {
          setRejectReasonOpen(false);
          setRejectTripId(null);
        }}
        onSubmit={handleConfirmReject}
      />

      {/* ── Re-Allot Reason Dialog ── */}
      <ReAllotReasonDialog
        open={reAllotReasonOpen}
        onClose={() => {
          setReAllotReasonOpen(false);
          setReAllotTripRow(null);
          setCurrentAssignedDriverId(null);
        }}
        onSubmit={handleReAllotReasonConfirm}
      />
    </div>
  );
}
